import axios from 'axios';
import Loader from '../../../library/intranet/loader';
import store from '../../../store';
import {errorHandler, sendPopupRequest, XHR_STATUS_DEFAULT} from '@/utils/portuna';
import EntityAssignTasks from '../../../views/employee/components/EntityAssignTasks.vue';
import EntityDeletePopUp from '../../../views/employee/components/EntityDeletePopUp.vue';
import {Popup, popupDefaultCancelButton} from '../../popup';
import {isAllowedModule} from '../../user';
import {NoOperation} from '../../utils';
import websocket from '../../websocket';
import {WEBSOCKET_ACTION_EMPLOYEE_EDITED} from '../../websocket/actions';

export const telephoneStatuses: Record<
    EmployeeTelephoneListStatus,
    {
        icon: string,
        color?: string,
        class?: string,
        title: string,
        onlyTimeClock?: boolean
        showAbsent?: boolean
        groupWith?: number
    }
> = {
    default: {
        icon: 'stop_circle',
        color: '#b64c36',
        title: 'Abwesend',
        onlyTimeClock: true,
    },
    0: {
        icon: 'fiber_manual_record',
        color: '#3b8136',
        title: 'Anwesend',
        onlyTimeClock: true,
    },
    0.2: {
        icon: 'home',
        color: '#3b8136',
        title: 'Mobile Arbeit (Anwesend)',
        groupWith: 0,
        onlyTimeClock: true,
    },
    1: {
        icon: 'local_hospital',
        class: 'local-hospital-icon',
        title: 'Krank',
        showAbsent: true,
    },
    1.2: {
        icon: 'local_hospital',
        class: 'local-hospital-icon',
        title: 'Krank (Berufsschule)',
        groupWith: 1,
        showAbsent: true,
    },
    2: {
        icon: 'school',
        color: '#b64c36',
        title: 'Berufsschule',
        showAbsent: true,
    },
    3: {
        icon: 'pause',
        color: '#b85900',
        title: 'Pause',
        onlyTimeClock: true,
    },
    3.2: {
        icon: 'home',
        color: '#b85900',
        title: 'Mobile Arbeit (Pause)',
        groupWith: 3,
        onlyTimeClock: true,
    },
    5: {
        icon: 'flight_takeoff',
        color: '#b64c36',
        title: 'Urlaub',
        showAbsent: true,
    },
    6: {
        icon: 'nature_people',
        color: '#b64c36',
        title: 'Außentermin',
        showAbsent: true,
    },
    7: {
        icon: 'local_library',
        color: '#b64c36',
        title: 'Fortbildung',
        showAbsent: true,
    },
    8: {
        icon: 'school',
        color: '#b64c36',
        title: 'Seminar',
        showAbsent: true,
    },
    9: {
        icon: 'block',
        color: '#b64c36',
        title: 'Sonstiges',
        showAbsent: true,
    },
    10: {
        icon: 'access_time',
        color: '#b64c36',
        title: 'Stundenabbau',
        showAbsent: true,
    },
};

export const EMPLOYEE_SHOW_SETTINGS = {
    'show_personal_data': { // View and Edit
        label: 'Persönliche Daten',
        help: '(z.B. Geburtstag)',
    },
    'show_employment_contract_employment': { // View and Edit
        label: 'Beschäftigung und Ausbildung, Qualifikation',
        help: '(Qualifikation: z.B. Ausbilder)',
    },
    'show_employment_contract_contact': { // Edit only
        label: 'Ansprechpartner',
        help: '(z.B. Ersthelfer, Brandschutzhelfer)',
    },
    'show_contact_data': { // View and Edit
        label: 'Kontaktdaten',
        help: 'Gesetzliche Vertretung',
    },
    'show_finances_data': { // View and Edit
        label: 'Finanzen',
        help: 'Bankverbindung, Steuer, Versicherung, Freiwillige Sozialversicherung',
    },
    'show_further_employment': { // View and Edit
        label: 'Weitere Beschäftigung',
        help: 'Vorbeschäftigung, Nebenbeschäftigung',
    },
    'show_knowledge': { // View and Edit
        label: 'Kenntnisse',
        help: 'Fachkenntnisse, Kompetenzen, Sprache, IT-Kenntnisse, Führerscheine, Zertifikate',
    },
    'show_salary_data': { // View and Edit
        label: 'Gehaltsdaten',
        help: '',
    },
};

export function getEmployeeStateOptions(withFuture: boolean = false)
{
    let options: SelectOption[] = [
        {
            value: 1,
            label: 'Aktuelle Mitarbeiter',
        },
        {
            value: 0,
            label: 'Ehemalige Mitarbeiter',
        },
    ];

    if (withFuture)
    {
        options.push({
            value: 2,
            label: 'Zukünftige Mitarbeiter',
        });
    }

    return options;
}

export function deleteEntity(entry: EntityListEntry | HierarchyEntityEntry | EntityViewEntry): Promise<void>
{
    return new Promise((resolve, reject) =>
    {
        let popup = new Popup({
            title: 'Abteilung löschen',
            titleIcon: 'delete',
            size: 'small',
            buttons: [popupDefaultCancelButton],
            component: EntityDeletePopUp,
            componentProps: {
                id: entry.id,
                name: entry.name,
            },
            events: {
                deleted: () =>
                {
                    popup.close();
                    resolve();
                },
                canceled: reject,
            },
        });

        popup.open();
    });
}

export function changeEntityStaus(entry: EntityListEntry | EntityViewEntry)
{
    const url = `/employee/entity/change-status/id/${entry.id}`;

    axios.get(url + '/check/1').then(response =>
    {
        if (response.data.status !== XHR_STATUS_DEFAULT)
        {
            throw response;
        }

        let data              = response.data.data;
        let assignedTaskCount = data.assignedTaskCount;

        const actionTerm = entry.active ? 'deaktivieren' : 'aktivieren';
        const icon       = entry.active ? 'remove_circle' : 'check_circle';

        let assignTaskData: Record<string, number> = {
            owner: 0,
            entity_id: 0,
        };

        if (entry.active && assignedTaskCount)
        {
            const popup = new Popup({
                title: 'Abteilung ' + actionTerm,
                titleIcon: icon,
                size: 'small',
                component: EntityAssignTasks,
                componentProps: {
                    assignedTaskCount,
                    entityId: entry.id,
                    excludeEntityId: entry.id,
                    asPopup: true,
                },
                events: {
                    'saved': () =>
                    {
                        entry.active = entry.active ? 0 : 1;
                    },
                },
            });

            popup.open();
        }
        else
        {
            sendPopupRequest({
                type: 'changeStatus' + (entry.active ? 'Deactivate' : 'Activate'),
                url,
                data: assignTaskData,
                message: {
                    headline: 'Abteilung ' + actionTerm,
                    message: 'Sind Sie sicher, dass Sie die Abteilung <strong>' + entry.name + '</strong> ' + actionTerm + ' möchten?',
                },
                options: Object.assign({
                    textIcon: icon,
                    titleIcon: icon,
                    textIconClass: 'text-muted',
                    buttonOkay: {
                        title: 'Abteilung ' + actionTerm,
                        icon,
                        type: entry.active ? 'danger' : 'success',
                    },
                }),
            })
            .then(() =>
            {
                entry.active = entry.active ? 0 : 1;
            })
            .catch(NoOperation);
        }
    })
    .catch(errorHandler);
}

export function getEmployeeBasicBreadcrumb(): Breadcrumb
{
    let hasModule = isAllowedModule('employee');

    if (hasModule)
    {
        return {
            label: 'Personal',
            href: {name: 'employee-home'},
        };
    }

    return {
        label: 'Dashboard',
        href: {name: 'employee-dashboard'},
    };
}

export function addEmployeeChangeListener(): void
{
    websocket.getManager()
    .then(manager =>
    {
        manager
        .listen(WEBSOCKET_ACTION_EMPLOYEE_EDITED, wsUpdateEmployees);
    })
    .catch(NoOperation);
}

export function removeEmployeeChangeListener(): void
{
    websocket.getManager()
    .then(manager =>
    {
        manager
        .remove(WEBSOCKET_ACTION_EMPLOYEE_EDITED, wsUpdateEmployees);
    })
    .catch(NoOperation);
}

export function exportPopup(url: string, employees: Nullable<number[]> = [], additionalQueryParams: Record<string, any> = {}, popupOptions: PopupOptions = {})
{
    const params = Object.assign({}, additionalQueryParams, employees ? {'ids': employees} : {});

    const popup = new Popup(Object.assign({
        title: 'Exportieren',
        titleIcon: 'info',
        text: 'Wählen Sie den Export als Excel-Datei oder PDF-Datei',
        textIcon: 'file_download',
        textIconClass: 'text-success',
        size: 'small',
        buttons: [
            popupDefaultCancelButton,
            {
                title: 'Excel-Export',
                icon: 'file_download',
                type: 'success',
                cmd: () =>
                {
                    Loader.show('Bitte warten Sie, während die Excel-Datei erstellt wird.');

                    axios.post(url + '/target/excel', params).then(({data: {data}}) =>
                    {
                        if (data && data.filepath)
                        {
                            window.open('/employee/statistic/download-export-file/filepath/' + btoa(data.filepath) + '/filename/' + data.filename, '_blank');
                        }
                    })
                    .catch(errorHandler)
                    .finally(() =>
                    {
                        Loader.hide();
                        popup.close();
                    });
                },
            },
            {
                title: 'PDF-Export',
                icon: 'picture_as_pdf',
                type: 'success',
                cmd: () =>
                {
                    Loader.show('Bitte warten Sie, während die PDF-Datei erstellt wird.');

                    axios.post(url + '/target/pdf', params).then(({data: {data}}) =>
                    {
                        if (data && data.filepath)
                        {
                            window.open('/employee/statistic/download-export-file/filepath/' + btoa(data.filepath) + '/filename/' + data.filename + '/download/inline', '_blank');
                        }
                    })
                    .catch(errorHandler)
                    .finally(() =>
                    {
                        Loader.hide();
                        popup.close();
                    });
                },
            },
        ],
    }, popupOptions));

    popup.open();
}

function wsUpdateEmployees(data: WebsocketCallbackPayload<EmployeeTelephoneListWebSocketEntryMultiple>): void
{
    store.dispatch('telephoneToolbar/updateEmployees', data).then(NoOperation);
}